<template>
	<div v-if="chartData" class="piechart">
		<Pie :options="chartOptions" :data="chartData" />
	</div>
</template>

<script lang="ts">
import { Pie } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, ArcElement, CategoryScale, LinearScale } from 'chart.js';
import { DecisionState, StatisticDecisionResponseItem, DecisionType } from '@/types/types';
import { defineComponent, PropType } from 'vue';

ChartJS.register(Title, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

export default defineComponent({
	name: 'PieChart',
	components: { Pie },
	props: {
		smallSenateData: {
			type: Object as PropType<StatisticDecisionResponseItem[] | null>,
		},
		werberatData: {
			type: Object as PropType<StatisticDecisionResponseItem[] | null>,
		},
		decisions: {
			type: Object as PropType<StatisticDecisionResponseItem[] | null>,
			required: true,
		},
		type: {
			type: String as PropType<DecisionType>,
			required: true,
		},
		title: {
			type: String,
		},
	},
	data() {
		return {
			chartOptions: {
				borderWidth: 5,
				borderColor: '#f4f5fc',
				responsive: true,
				plugins: {
					legend: {
						display: false,
					},
					title: {
						display: true,
						text: this.title,
						color: '#2e3a50',
						padding: 20,
						font: {
							size: 22,
							weight: 'bold',
							family: 'Roboto',
						},
					},
					tooltip: {
						callbacks: {
							label: (context) => {
								const totalCount = this.getDecisionCount();
								const count = context.parsed;
								let parsedTitle = '';
								switch (this.title) {
									case 'Werberat':
										parsedTitle = 'Werberät:innen';
										break;
									case 'Junger Werberat':
										parsedTitle = 'jungen Werberät:innen';
										break;
									case 'Kleiner Senat A':
									case 'Kleiner Senat B':
										parsedTitle = 'Senator:innen';
										break;
									case 'Gesamt':
										parsedTitle = 'Entscheidungen';
										break;
								}
								return `${count} von ${totalCount} ${parsedTitle}`;
							},
							footer: (context) => {
								const totalCount = this.getDecisionCount();
								const count = context[0].parsed;
								const percentage = ((count / totalCount) * 100).toFixed(0);
								return `${percentage}%`;
							},
						},
						displayColors: false,
					},
				},
			} as any,
		};
	},
	computed: {
		chartData() {
			if (!this.decisions || this.decisions.length == 0) return null;

			if (this.type === DecisionType.SmallSenate) {
				let decisionData = [0, 0];
				this.decisions.forEach((d) => {
					switch (d.decisionState) {
						case DecisionState.GoSmallSenate:
							decisionData[0] += d.count;
							break;
						case DecisionState.StopSmallSenate:
							decisionData[1] += d.count;
							break;
					}
				});

				return {
					labels: [
						this.$getStateFullLabel('decision', DecisionState.GoSmallSenate, false),
						this.$getStateFullLabel('decision', DecisionState.StopSmallSenate, false),
					],
					datasets: [
						{
							data: decisionData,
							backgroundColor: ['#B71B1B', '#168318'],
							hoverOffset: 4,
						},
					],
				};
			} else if (this.type === DecisionType.Werberat || this.type === DecisionType.JungerWerberat) {
				let decisionData = [0, 0, 0, 0];
				this.decisions.forEach((d) => {
					switch (d.decisionState) {
						case DecisionState.Biased:
							decisionData[0] += d.count;
							break;
						case DecisionState.NoReason:
							decisionData[1] += d.count;
							break;
						case DecisionState.Awareness:
							decisionData[2] += d.count;
							break;
						case DecisionState.Stop:
							decisionData[3] += d.count;
							break;
					}
				});
				return {
					labels: [
						this.$getStateFullLabel('decision', DecisionState.Biased, false),
						this.$getStateFullLabel('decision', DecisionState.NoReason, false),
						this.$getStateFullLabel('decision', DecisionState.Awareness, false),
						this.$getStateFullLabel('decision', DecisionState.Stop, false),
					],
					datasets: [
						{
							data: decisionData,
							backgroundColor: ['#2E3A50', '#168318', '#ED8B21', '#B71B1B'],
							hoverOffset: 4,
						},
					],
				};
			} else {
				return null;
			}
		},
	},
	methods: {
		getDecisionCount() {
			if (!this.decisions || this.decisions.length == 0) return 0;
			return this.decisions
				.filter((d) => d.decisionState !== DecisionState.Null && d.decisionState !== DecisionState.UserAbsent)
				.reduce((sum, item) => sum + item.count, 0);
		},
	},
});
</script>
